// Kompilasi & eksekusi paintCode prosedural (sandbox minimal) + renderer bangunan bebas.
// Dipisahkan dari engine.js agar engine inti lebih ramping.
import { TILE_W, TILE_H } from "./iso";
import { cssCol, shade } from "./color";

const FLOOR_H = 36; // px per lantai (skala 2x; referensi baris jendela, BUKAN batas tinggi)
export { FLOOR_H };
// Referensi lebar tile untuk aset bergerak (kendaraan & karakter) — lebih kecil dari
// TILE_W agar proporsional terhadap bangunan. Sinkron dengan TRAFFIC.unitTile (trafficCore.js).
export const MOVER_T = 70;

// Tinggi referensi bangunan — TIDAK dibatasi kaku oleh floors*floorH.
export function refHeight(floors, w, h, zoom) {
  const span = (w + h) * (TILE_W / 2) * zoom;
  return Math.max((floors || 0) * FLOOR_H * zoom, span * 0.22);
}

// ---- Kompilasi paintCode dengan cache + sandbox minimal ----
const paintCache = {};
const FORBIDDEN = /(\b(window|document|globalThis|localStorage|sessionStorage|location|navigator)\s*[.\[])|(\b(fetch|XMLHttpRequest|require|eval|Function|Worker|Promise|setTimeout|setInterval)\s*\()|\b(import|async|await)\b/;
// ---- Helper NAMA BANGUNAN "3D LETTER BOX" (huruf timbul tanpa papan latar) ----
// Rumus BAKU presisi (anti-melayang & anti-raksasa): origin dipindah TEPAT ke
// titik tempel dinding (anchorX, anchorY) LALU teks digambar di (0,0) — TANPA
// double-translate. Ukuran font dikunci proporsional lebar dinding (maxWallWidth)
// + auto-fit, sehingga teks tidak pernah raksasa atau meluber keluar gedung.
//   draw3DLetterBox(ctx, anchorX, anchorY, text, faceSide, maxWallWidth, color, depthColor, isNight)
//     faceSide "SW" (dinding hadap kiri-bawah → miring +0.5) / "SE" (kanan-bawah → -0.5)
//   drawSignboard(...) = wrapper kompatibel untuk 88 pemanggilan lama.
// OVERRIDE per-bangunan (edit editor): U.signEdit {text,fg,glow,fs} menggantikan argumen aset.
const SIGNBOARD_HELPER = `
// Helper detail dinding 2D RATA (flat decal) — menempel 100% di permukaan dinding
// SW ("S") atau SE ("E"). Dipakai untuk jendela/pintu/rolling door/pita fasad
// (BUKAN balok 3D). face: "S" (top→bottom) atau "E" (top→right).
function drawWallQuad(ctx, P, face, u1, v1, u2, v2, col) {
  var A = (face === "E") ? P.top : P.left;
  var B = (face === "E") ? P.right : P.bottom;
  var h = P.height;
  function gp(u, v) { return [A[0] + (B[0] - A[0]) * u, A[1] + (B[1] - A[1]) * u - v * h]; }
  var a = gp(u1, v1), b = gp(u2, v1), c = gp(u2, v2), d = gp(u1, v2);
  ctx.fillStyle = col; ctx.beginPath(); ctx.moveTo(a[0], a[1]); ctx.lineTo(b[0], b[1]); ctx.lineTo(c[0], c[1]); ctx.lineTo(d[0], d[1]); ctx.closePath(); ctx.fill();
}
// Balok solid tertutup (4 dinding + atap rapat). Alias box3 dengan shading 3 sisi.
function isoBox(ctx, P, U, u, v, z, wu, dv, h, col) {
  function pt(u2, v2, z2) { return [P.top[0] + u2 * (P.right[0] - P.top[0]) + v2 * (P.left[0] - P.top[0]), P.top[1] + u2 * (P.right[1] - P.top[1]) + v2 * (P.left[1] - P.top[1]) - z2]; }
  function q(a, b, c, d, f) { ctx.fillStyle = f; ctx.beginPath(); ctx.moveTo(a[0], a[1]); ctx.lineTo(b[0], b[1]); ctx.lineTo(c[0], c[1]); ctx.lineTo(d[0], d[1]); ctx.closePath(); ctx.fill(); }
  var p1 = pt(u, v, z + h), p2 = pt(u + wu, v, z + h), p3 = pt(u + wu, v + dv, z + h), p4 = pt(u, v + dv, z + h);
  var b1 = pt(u, v + dv, z), b2 = pt(u + wu, v + dv, z), b3 = pt(u + wu, v, z);
  q(p4, p3, b2, b1, U.shade(col, 0.85)); q(p3, p2, b3, b2, U.shade(col, 0.68)); q(p1, p2, p3, p4, U.shade(col, 1.0));
}
function draw3DLetterBox(ctx, anchorX, anchorY, text, faceSide, maxWallWidth, color, depthColor, isNight) {
  var str = String(text == null ? "" : text);
  if (!str) return;
  function SC(c, dflt) {
    if (c == null) return dflt;
    if (Array.isArray(c)) {
      try { return U.cssCol(c); } catch (e) { return "rgb(" + (c[0] | 0) + "," + (c[1] | 0) + "," + (c[2] | 0) + ")"; }
    }
    return c;
  }
  var face = SC(color, "#FFFFFF");
  var depth = SC(depthColor, "#1E293B");
  var night = (typeof isNight === "number") ? isNight : (isNight ? 1 : 0);
  if (!night && typeof U !== "undefined" && U.night) night = U.night;
  ctx.save();
  // 1. Pindahkan origin TEPAT ke titik permukaan dinding/fasad/kanopi
  ctx.translate(anchorX, anchorY);
  // 2. Miringkan sumbu X mengikuti kemiringan dinding isometrik 2:1
  var skewY = (faceSide === "SE") ? -0.5 : 0.5;
  ctx.transform(1, skewY, 0, 1, 0, 0);
  // 3. Kunci ukuran font proporsional lebar dinding (ANTI-RAKSASA)
  var mww = (typeof maxWallWidth === "number" && maxWallWidth > 0) ? maxWallWidth : 60;
  var fontSize = Math.max(4, Math.min(11, mww * 0.16));
  ctx.font = "900 " + fontSize + "px 'Inter','Arial Black',sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  var measured = ctx.measureText(str).width;
  if (measured > mww * 0.85 && measured > 0) {
    fontSize = fontSize * ((mww * 0.85) / measured);
    ctx.font = "900 " + fontSize + "px 'Inter','Arial Black',sans-serif";
  }
  // 4. Ketebalan 3D (extrude ke belakang dinding) — digambar di sekitar (0,0)
  var depthOffset = Math.max(0.8, fontSize * 0.12);
  var dx = (faceSide === "SE") ? -depthOffset : depthOffset;
  var dy = depthOffset * 0.5;
  ctx.fillStyle = depth;
  ctx.fillText(str, dx, dy);
  ctx.fillText(str, dx * 0.5, dy * 0.5);
  // 5. Huruf utama di (0,0) + glow malam
  if (night > 0.25) {
    ctx.shadowColor = face;
    ctx.shadowBlur = fontSize * 0.6;
  }
  ctx.fillStyle = face;
  ctx.fillText(str, 0, 0);
  ctx.shadowBlur = 0;
  ctx.restore();
}
// Wrapper kompatibel: drawSignboard(ctx, x, y, text, style, side)
// (x,y) dari aset lama = pojok KIRI-ATAS area papan. Diterjemahkan ke anchor tengah.
function drawSignboard(ctx, x, y, text, style, side) {
  var st = (typeof style === "string") ? { type: style } : (style || {});
  var SE2 = (typeof U !== "undefined" && U.signEdit) || null;
  var txt = text, fg = st.fg, glow = st.glow, fsU = st.fs;
  if (SE2) {
    if (SE2.text != null && SE2.text !== "") txt = SE2.text;
    if (SE2.fg) fg = SE2.fg;
    if (SE2.glow) glow = SE2.glow;
    if (SE2.fs != null) fsU = SE2.fs;
  }
  var T = (typeof U !== "undefined" && U.tileW) || 80;
  var zc = (typeof U !== "undefined" && U.zoom) || 1;
  function SC(c, dflt) {
    if (c == null) return dflt;
    if (Array.isArray(c)) {
      try { return U.cssCol(c); } catch (e) { return "rgb(" + (c[0] | 0) + "," + (c[1] | 0) + "," + (c[2] | 0) + ")"; }
    }
    return c;
  }
  var fgC = SC(fg, "#FF3B30");
  var glowC = SC(glow, fgC);
  // gelapkan untuk sisi tebal
  function darken(c, f) {
    var m = /^#([0-9a-f]{6})/i.exec(c);
    if (m) {
      var n = parseInt(m[1], 16);
      var r = Math.round(((n >> 16) & 255) * f), g = Math.round(((n >> 8) & 255) * f), b = Math.round((n & 255) * f);
      return "rgb(" + r + "," + g + "," + b + ")";
    }
    return c;
  }
  var depthC = SC(st.depthColor, darken(fgC, 0.42));
  var night = (typeof U !== "undefined" && U.night) || 0;
  // Lebar dinding = lebar papan lama (st.w) * T, dibatasi wajar (0.3T..1.3T).
  var wallW = (st.w != null ? st.w : 0.7) * T;
  wallW = Math.max(0.3 * T, Math.min(1.3 * T, wallW));
  // Anchor = titik tengah area papan lama: (x + w/2, y + h/2). h dari st.h.
  var h0 = (st.h != null ? st.h : 0.24) * T;
  var ax = x + wallW / 2;
  var ay = y + h0 * 0.5;
  // tipe yard: berdiri di halaman dengan tiang kecil; angkat anchor di atas tiang
  if ((st.type || "wall") === "yard") {
    var ph = Math.min((st.poleH != null ? st.poleH : 0.30) * T, 0.55 * T);
    ctx.fillStyle = "#334155";
    ctx.fillRect(ax - 1.4 * zc, y - ph, 2.8 * zc, ph);
    ay = y - ph + h0 * 0.4;
  }
  // dukung override ukuran huruf via fsU (satuan T) → paksa maxWallWidth agar font sesuai
  // (font = 0.16 * maxWallWidth). Jika fsU ada, maxWallWidth = fsU*T/0.16.
  if (fsU != null) {
    var wantFs = fsU * T;
    wallW = wantFs / 0.16;
  }
  var nightF = night > 0.25 ? 1 : 0;
  // warna face saat malam memakai glow agar "menyala"; siang pakai fg
  draw3DLetterBox(ctx, ax, ay, txt, (side === "SE") ? "SE" : "SW", wallW, (nightF ? glowC : fgC), depthC, nightF);
}
// ---- PAPAN NAMA GAYA JAWSEL: drawNamePlate(ctx, P, U, face, a1, a2, f, z0, h, text, plateCol, textCol) ----
// Teks TEBAL terpusat pada pelat/sabuk yang menempel RATA di bidang dinding, auto-fit (tidak pernah
// meluber), mengikuti kemiringan isometrik dinding. face "S" = dinding kiri-depan (bidang v=f,
// rentang u a1..a2) | "E" = dinding kanan-depan (bidang u=f, rentang v a1..a2). z0 = dasar pelat (px),
// h = tinggi pelat (px). plateCol null → teks langsung di dinding (tanpa pelat), seperti "PEMADAM KEBAKARAN".
// Menghormati override editor U.signEdit {text, fg, glow, fs}.
function drawNamePlate(ctx, P, U, face, a1, a2, f, z0, h, text, plateCol, textCol) {
  function pt(u, v, z) { return [P.top[0] + u * (P.right[0] - P.top[0]) + v * (P.left[0] - P.top[0]), P.top[1] + u * (P.right[1] - P.top[1]) + v * (P.left[1] - P.top[1]) - z]; }
  function q(a, b, c, d, fill) { ctx.fillStyle = fill; ctx.beginPath(); ctx.moveTo(a[0], a[1]); ctx.lineTo(b[0], b[1]); ctx.lineTo(c[0], c[1]); ctx.lineTo(d[0], d[1]); ctx.closePath(); ctx.fill(); }
  function SC(c, dflt) {
    if (c == null) return dflt;
    if (Array.isArray(c)) { try { return U.cssCol(c); } catch (e) { return "rgb(" + (c[0] | 0) + "," + (c[1] | 0) + "," + (c[2] | 0) + ")"; } }
    return c;
  }
  var S = (face !== "E");
  var R = function (z) { return S ? [pt(a1, f, z), pt(a2, f, z)] : [pt(f, a1, z), pt(f, a2, z)]; };
  var b0 = R(z0), t0 = R(z0 + h);
  var SE2 = U.signEdit || null;
  var str = text, fg = textCol, glow = null, fsU = null;
  if (SE2) {
    if (SE2.text != null && SE2.text !== "") str = SE2.text;
    if (SE2.fg) fg = SE2.fg;
    if (SE2.glow) glow = SE2.glow;
    if (SE2.fs != null) fsU = SE2.fs;
  }
  if (plateCol != null) {
    var sh0 = R(z0 - h * 0.14), lip = R(z0 + h * 0.9);
    q(sh0[0], sh0[1], b0[1], b0[0], "rgba(0,0,0,0.12)");                 // bayangan tipis di bawah pelat
    q(b0[0], b0[1], t0[1], t0[0], U.shade(plateCol, S ? 0.92 : 0.78));  // pelat mengikuti shading dinding
    q(lip[0], lip[1], t0[1], t0[0], "rgba(255,255,255,0.22)");          // bibir atas pelat lebih terang
  }
  str = String(str == null ? "" : str);
  if (!str) return;
  var cx = (b0[0][0] + t0[1][0]) / 2, cy = (b0[0][1] + t0[1][1]) / 2;
  var wpx = Math.abs(b0[1][0] - b0[0][0]);
  var fs = fsU != null ? fsU * ((U.tileW) || 80) : h * 0.8;
  ctx.save();
  ctx.translate(cx, cy);
  ctx.transform(1, S ? 0.5 : -0.5, 0, 1, 0, 0);
  ctx.font = "900 " + fs + "px 'Inter','Arial Black',sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  var m = ctx.measureText(str).width, maxW = wpx * 0.86;
  if (m > maxW && m > 0) { fs = fs * (maxW / m); ctx.font = "900 " + fs + "px 'Inter','Arial Black',sans-serif"; }
  var night = U.night || 0;
  var col = SC(fg, "#333333");
  if (night > 0.25) { col = SC(glow || fg, col); ctx.shadowColor = col; ctx.shadowBlur = fs * 0.6; }
  ctx.fillStyle = "rgba(0,0,0,0.18)";
  ctx.fillText(str, fs * 0.04, fs * 0.05); // bayangan huruf tipis agar tegas
  ctx.fillStyle = col;
  ctx.fillText(str, 0, 0);
  ctx.shadowBlur = 0;
  ctx.restore();
}
`;
export function compilePaint(bid, code, dir, { noShadow = false } = {}) {
  const key = (dir ? `${bid}__${dir}` : bid) + (noShadow ? "__ns" : "");
  if (paintCache[key]?.src === code) return paintCache[key].fn;
  if (!code || FORBIDDEN.test(code)) return null;
  try {
    let body = code;
    // Tanpa bayangan tempel: KIT kendaraan menggambar bayangan via function shadow(...) —
    // dinetralkan untuk entitas di udara (pesawat) agar bayangan tidak ikut melayang.
    if (noShadow) body = body.replace(/function\s+shadow\s*\([^)]*\)\s*\{[^{}]*\}/, "function shadow(){}");
    // Rotasi sumbu lokal untuk kendaraan sesuai arah hadap.
    // PaintCode kendaraan MENDEFINISIKAN function iso(lx,ly,lz) sendiri,
    // jadi rotasi diterapkan dengan MENGGANTI definisi iso() milik paintCode
    // agar argumen lx/ly di-flip/swap sesuai `dir` sebelum diproyeksikan.
    if (dir) {
      const rotMap = { SE: "[lx,ly,lz]", NW: "[-lx,-ly,lz]", SW: "[-ly,lx,lz]", NE: "[ly,-lx,lz]" };
      const rotExpr = rotMap[dir] || rotMap.SE;
      // ganti definisi function iso(lx,ly,lz){...} dengan versi berotasi
      body = body.replace(
        /function\s+iso\s*\(\s*lx\s*,\s*ly\s*,\s*lz\s*\)\s*\{/,
        `function iso(lx, ly, lz) { var __r = ${rotExpr}; lx = __r[0]; ly = __r[1]; lz = __r[2];`
      );
    }
    // Jika AI menulis deklarasi fungsi bernama, panggil otomatis di akhir
    const m = body.match(/function\s+(\w+)\s*\(\s*ctx/);
    if (m) body = body + `\n${m[1]}(ctx, P, U);`;
    // KIT aset bergerak (kendaraan/karakter) mengantri bentuk 3D di Q dan menggambarnya
    // lewat __flush() dengan urutan painter's otomatis per arah hadap.
    body = body + `\nif(typeof __flush==="function")__flush();`;
    const fn = new Function("ctx", "P", "U", `"use strict";${SIGNBOARD_HELPER}${body}`);
    paintCache[key] = { src: code, fn };
    return fn;
  } catch { return null; }
}

// State kota kosong (pioneer state)



// Lukis satu bangunan di posisi layar bebas (untuk galeri/preview)
export function paintBuildingAt(ctx, def, cx, cy, zoom, opts = {}) {
  const w = def?.w || 1;
  const h = def?.h || 1;
  const floors = def?.floors ?? 1;
  const floorH = FLOOR_H * zoom;
  const height = refHeight(floors, w, h, zoom); // uncapped: proporsional luas tapak
  const hw = (TILE_W / 2) * zoom;
  const hh = (TILE_H / 2) * zoom;
  // Sudut footprint isometrik (berlian) di sekitar pusat
  const P = {
    top: [cx, cy - ((w + h) * hh) / 2],
    right: [cx + (w * hw), cy + ((w - h) * hh) / 2],
    bottom: [cx, cy + ((w + h) * hh) / 2],
    left: [cx - (h * hw), cy + ((w - h) * hh) / 2],
    height
  };

  // Satuan skala: tileW asli (T = TILE_W*zoom) untuk bangunan; entitas hidup
  // (Kendaraan & Karakter) dikunci ke referensi T=MOVER_T*zoom supaya proporsional
  // terhadap bangunan & jalan: KIT aset bergerak memakai 1 unit = T/80 px
  // (sedan 40 unit ≈ 0.49 tile, bus 58 ≈ 0.71 tile, warga ≈ 13px pada zoom 1).
  const isChar = def && def.category === "Karakter";
  const isVeh  = def && def.category === "Kendaraan";
  const tileWRef = (isChar || isVeh ? MOVER_T : TILE_W) * zoom;
  if (isChar) {
    const ehw = 7 * zoom, ehh = 3.5 * zoom;
    P.top = [cx, cy - ehh];
    P.right = [cx + ehw, cy];
    P.bottom = [cx, cy + ehh];
    P.left = [cx - ehw, cy];
  }
  const U = { zoom, floors, floorH, span: (w + h) * hw, tileW: tileWRef, wall: def?.wall || [210,195,170], roof: def?.roof || [170,70,55], cssCol, shade, def, night: opts.night || 0, time: opts.time || 0 };
  // Untuk aset bergerak: rotasi sumbu lokal iso() sesuai arah hadap agar bodi
  // kendaraan/warga menghadap lurus arah jalan (tidak jalan nyamping).
  const dir = (isVeh || isChar) ? (opts.dir || "SE") : null;
  const fn = def?.paintCode ? compilePaint(def.bid, def.paintCode, dir, { noShadow: !!opts.noShadow }) : null;
  if (fn) {
    try { fn(ctx, P, U); return; } catch { /* fallback */ }
  }
  // fallback: box 3 sisi
  ctx.fillStyle = shade(U.wall, 0.72);
  ctx.beginPath();
  ctx.moveTo(...P.left); ctx.lineTo(...P.bottom);
  ctx.lineTo(P.bottom[0], P.bottom[1] - P.height); ctx.lineTo(P.left[0], P.left[1] - P.height);
  ctx.closePath(); ctx.fill();
  ctx.fillStyle = shade(U.wall, 0.86);
  ctx.beginPath();
  ctx.moveTo(...P.bottom); ctx.lineTo(...P.right);
  ctx.lineTo(P.right[0], P.right[1] - P.height); ctx.lineTo(P.bottom[0], P.bottom[1] - P.height);
  ctx.closePath(); ctx.fill();
  ctx.fillStyle = cssCol(U.roof);
  ctx.beginPath();
  ctx.moveTo(P.top[0], P.top[1] - P.height); ctx.lineTo(P.right[0], P.right[1] - P.height);
  ctx.lineTo(P.bottom[0], P.bottom[1] - P.height); ctx.lineTo(P.left[0], P.left[1] - P.height);
  ctx.closePath(); ctx.fill();
}
